import React, { useEffect, useRef } from "react";

/**
 * CodeRain — a subtle matrix-style canvas of falling programming glyphs.
 * Designed to live behind the hero. Uses a low-alpha gold drip on a transparent
 * background so the editorial layer above stays legible.
 */
const GLYPHS =
  "</>{};()=>def class import async await fn let var const return print if else for while True False null;0123456789".split(
    "",
  );

export default function CodeRain({ density = 1, className = "" }) {
  const ref = useRef(null);
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (reduce) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let drops = [];

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cols = Math.floor(rect.width / 18);
      drops = Array.from({ length: Math.floor(cols * density) }, () => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        speed: 0.4 + Math.random() * 0.9,
        alpha: 0.06 + Math.random() * 0.18,
      }));
    };

    resize();
    window.addEventListener("resize", resize);

    const tick = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      ctx.font = "12px 'JetBrains Mono', monospace";
      for (const d of drops) {
        ctx.fillStyle = `rgba(197, 160, 89, ${d.alpha})`;
        const g = GLYPHS[(Math.random() * GLYPHS.length) | 0];
        ctx.fillText(g, d.x, d.y);
        d.y += d.speed;
        if (d.y > h + 20) {
          d.y = -20;
          d.x = Math.random() * w;
          d.speed = 0.4 + Math.random() * 0.9;
          d.alpha = 0.06 + Math.random() * 0.18;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [density, reduce]);

  if (reduce) return null;

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${className}`}
    />
  );
}
